'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { MapPin, Target, X, Search as SearchIcon } from 'lucide-react';
import { usePlanningSearch } from '../state/PlanningSearchContext';
import { formatRadiusLabel } from '../../../lib/search/searchUtils';

const quickRadiusOptions = [0, 0.25, 0.5, 1, 2];

interface LocationRadiusControlProps {
  variant?: 'standalone' | 'panel';
  className?: string;
  showHeading?: boolean;
  compact?: boolean;
}

const LocationRadiusControl: React.FC<LocationRadiusControlProps> = ({
  variant = 'standalone',
  className,
  showHeading = true,
  compact = false,
}) => {
  const {
    location,
    setLocation,
    radiusKm,
    setRadius,
    pendingGeocode,
    radiusSearchSummary,
  } = usePlanningSearch();
  const [radiusWarning, setRadiusWarning] = useState(false);
  const radiusLabel = formatRadiusLabel(radiusKm);
  const exactFallbackActive = radiusSearchSummary.exactHasNoResultsButRadiusHasResults;

  const focusGlobalSearch = useCallback(() => {
    if (typeof window === 'undefined') return;
    const input = document.querySelector<HTMLInputElement>('input[data-global-search-input="true"]');
    if (input) {
      input.focus();
      window.requestAnimationFrame(() => input.select?.());
    }
  }, []);

  const containerClasses = [
    variant === 'standalone'
      ? 'pr-panel mt-4 p-4 sm:p-5'
      : 'grid gap-5',
    'relative z-10',
    className ?? '',
  ].join(' ').trim();

  const headingClass = variant === 'standalone'
    ? 'pr-section-label mb-4'
    : 'pr-section-label';

  const handleRadiusChange = useCallback((value: number) => {
    if (!location) {
      setRadiusWarning(true);
      return;
    }
    setRadius(value);
  }, [location, setRadius]);

  useEffect(() => {
    if (location) {
      setRadiusWarning(false);
    }
  }, [location]);

  return (
    <div className={containerClasses}>
      {showHeading && <p className={headingClass}>Search area</p>}

      <div className={compact ? 'grid gap-4' : 'grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]'}>
        <div className="min-w-0">
          <p className="pr-data text-[10px] uppercase tracking-[0.14em]">Search centre</p>
          <div className="mt-2 flex flex-col gap-3 border border-[var(--c-border)] bg-[var(--c-bg)] p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 text-sm leading-6">
              <MapPin className={`mt-0.5 h-4 w-4 shrink-0 ${location ? 'text-[var(--c-accent)]' : 'text-[var(--c-text-tertiary)]'}`} aria-hidden />
              <div className="min-w-0">
                <p className="truncate font-medium text-[var(--c-text-primary)]">
                  {location ? location.label : 'No specific location selected'}
                </p>
                <p className="text-[var(--c-text-secondary)]">
                  {location
                    ? 'The radius search is centred on this location.'
                    : 'Use the search bar to choose an address, street or postcode.'}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {location && (
                <button
                  type="button"
                  onClick={() => setLocation(null)}
                  className="pr-textlink"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={focusGlobalSearch}
                className={location ? 'pr-button pr-button--ghost' : 'pr-button'}
              >
                <SearchIcon className="h-3.5 w-3.5" aria-hidden />
                {location ? 'Change' : 'Set centre'}
              </button>
            </div>
          </div>
        </div>

        {pendingGeocode && (
          <div className={`flex items-center gap-2 text-xs text-[var(--c-text-secondary)] ${compact ? '' : 'xl:col-span-2'}`}>
            <span className="inline-flex h-3 w-3 animate-spin rounded-full border-2 border-[var(--c-accent-muted)] border-t-transparent" aria-hidden />
            Locating address...
          </div>
        )}

        <div className="min-w-0">
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="planning-radius-search" className="flex items-center gap-2 pr-data text-[10px] uppercase tracking-[0.14em]">
              <Target className="h-4 w-4 text-[var(--c-accent)]" aria-hidden />
              Radius
            </label>
            <span className="font-['Newsreader'] text-lg leading-none text-[var(--c-text-primary)]">
              {radiusLabel}
            </span>
          </div>
          {radiusWarning && !location && (
            <p className="mt-2 text-xs text-[var(--c-accent)]">
              Choose a full address from the search dropdown to use radius filtering.
            </p>
          )}
          <div className="mt-4">
            <input
              id="planning-radius-search"
              type="range"
              min={0}
              max={2}
              step={0.25}
              value={Math.min(radiusKm, 2)}
              onChange={(event) => handleRadiusChange(Number(event.target.value))}
              className="pr-radius-range w-full"
              aria-label="Search radius"
              aria-valuetext={radiusLabel}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {quickRadiusOptions.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => handleRadiusChange(value)}
                aria-pressed={radiusKm === value}
                className={`pr-chip ${radiusKm === value ? 'pr-chip--on' : ''}`}
              >
                {formatRadiusLabel(value)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`border-l-2 px-3 py-2 text-sm leading-6 ${exactFallbackActive ? 'border-[var(--c-accent)] bg-[var(--c-accent-muted)] text-[var(--c-text-primary)]' : 'border-[var(--c-border)] text-[var(--c-text-secondary)]'}`}>
        {exactFallbackActive
          ? `No applications were found at the exact search location. Showing ${radiusSearchSummary.radiusResultCount.toLocaleString()} result${radiusSearchSummary.radiusResultCount === 1 ? '' : 's'} within ${radiusSearchSummary.radiusLabel}.`
          : location
            ? radiusKm === 0
              ? 'Exact address only checks a tight 50 m area around the selected centre.'
              : `Showing applications within ${radiusLabel} of the selected centre.`
            : 'Radius controls become active once a search centre has been selected.'}
      </div>
    </div>
  );
};

export default LocationRadiusControl;
